import * as firebase from 'firebase/app';
import 'firebase/auth';
import * as _ from 'lodash';
import axios from 'axios';
import { getCurrentFirebaseUser } from '../util/firebase-auth';
import { getAuthQueryNoTenantQuery, getAuthV2Url } from '../configs/authV2';

export default class SessionsService {
  constructor($http, $log, StorageService, Restangular, $q, sweet, UnifiedLoginService) {
    'ngInject';
    this.$http = $http;
    this.$log = $log;
    this.StorageService = StorageService;
    this.Restangular = Restangular;
    this.identity = null;
    this.$q = $q;
    this.uriBase = 'v1/sessions';
    this.sweet = sweet;
    this.poll = false;
    firebase.auth().onAuthStateChanged((user) => {
      this.handleAuthStateChanged(user);
    });
    this.unifiedLoginService = UnifiedLoginService;
  }

  /** @access private */
  handleAuthStateChanged (firebaseUser) {
    this.firebaseUser = firebaseUser;

    if (this.firebaseUser) {
      var self = this;

      this.getIdentity().then(function(resp) {
        self.StorageService.set('userTenantPermits',  _.get(resp, 'user.userTenantPermits'));
      })
      .then(() => this.activatePoll())
      .catch(err => {
        console.error(`Failed to set permit on user change. (${err.message})`);
      })
    }
  }

  activatePoll  () {
    this.disablePoll();
    this.poll = true;
    this.runPoll();
  }

  disablePoll () {
    this.poll = false;
  }

  runPoll() {
    var self = this;
    self.getIdentity
    var poll = setTimeout(function () {
      if (self.poll == false) {
        clearTimeout(poll);
      } else {
        self.getIdentity().then(function(resp) {
          self.StorageService.set('userTenantPermits', resp.user.userTenantPermits)
        }).catch(err => {
          console.error(`Failed to set permit on user change. (${err.message})`);
        })
        self.runPoll();
      }
    }, 1500000); // Set poll timer to 20 minutes to allow the JWT token refresh
  }

  getAuthV2() {
    return axios.post(
      getAuthV2Url(),
      {
        query: getAuthQueryNoTenantQuery,
      },
      {
        headers: { 
          'Authorization': `Bearer ${this.getIdToken()}`,
          'Content-Type': 'application/json'
        },
      },
    );
  }

  getIdentity() {
    let promise;
    const doGet = () => this.Restangular.oneUrl('identity').get();
    if (this.firebaseUser) {
      promise = this.firebaseUser.getIdToken()
        .then(idToken => {
          this.setIdToken(idToken);
        })
        .then(() => doGet());
    } else {
      // fallback
      promise = getCurrentFirebaseUser()
        .then(user => {
          if (user) {
            return user.getIdToken();
          } else {
            console.debug('Unable to get user token');
          }
        })
        .then(token => this.setIdToken(token))
        .then(() => doGet());
    }
    return promise
      .then(resp => {
        this.identity = resp.user;
        return resp;
      })
      .catch(err => console.error(err));
  }

  isTenantAdmin(tenantId) {
    if (this.identity === null || !tenantId) {
      return false;
    }
    const tenant = _.find(this.identity.userTenantPermits, {tenantId: tenantId});
    return _.get(tenant, 'role.roleType') === 'ADMIN';
  }

  isManagingOrgAdmin(managingOrgId){
    if (!this.identity || !managingOrgId) {
      return false;
    }
    const managingOrg = _.find(
      this.identity.userOrgPermits, { managingOrg: { id:  managingOrgId }}
    );
    return _.get(managingOrg, 'role.roleType') === 'ADMIN';
  }

  /** @access private */
  setIdToken(idToken) {
    return this.StorageService.set('idToken', idToken)
  }

  setCurrentDistributorKey(value) {
    return this.StorageService.set('currentDistributorKey', value);
  }

  setCurrentDistributorName(value) {
    return this.StorageService.set('currentDistributorName', value);
  }

  setCurrentDistributorCode(value) {
    return this.StorageService.set('currentDistributorCode', value);
  }

  setIsTenant(user, distributorName) {
    let userOrgTenant = _.find(user.userOrgPermits, 'managingOrg.name', distributorName);
    let isTenant = userOrgTenant.role.roleType === 'USER' && !userOrgTenant.tenantSubscriber;

    return this.StorageService.set('isTenant', isTenant);
  }

  setCurrentSelectedTenantCode(code) {
    return this.StorageService.set('selectedTenantCode', code);
  }

  /**
   * 
   * @param {Boolean} value 
   * @returns 
   */
  setIsBeamAuth(value) {
    return this.StorageService.set('isBeamAuth', value);
  }

  getUserKey() {
    return this.StorageService.get('userKey');
  }

  isLoggedIn() {
    return !!this.getUserKey();
  }

  isStateCorrupt() {
    return Boolean(this.isLoggedIn() && !this.getIdToken());
  }

  getUserEmail() {
    return this.StorageService.get('userEmail');
  }

  getDistributors() {
    return this.StorageService.get('distributors');
  }

  getCurrentDistributorName() {
    return this.StorageService.get('currentDistributorName');
  }

  getCurrentDistributorKey() {
    return this.StorageService.get('currentDistributorKey');
  }

  getCurrentDistributorCode() {
    return this.StorageService.get('currentDistributorCode');
  }

  getDistributorsAsAdmin() {
    return this.StorageService.get('distributorsAsAdmin');
  }

  getIsAdmin() {
    return this.StorageService.get('isAdmin');
  }

  isAdminOrDistributorAdmin() {
    let distributors = this.getDistributorsAsAdmin();
    return Boolean((distributors && distributors.length) || this.getIsAdmin());
  }

  getUserInfo() {
    return this.StorageService.get('userInfo');
  }

  getUserTenantPermits() {
    return this.StorageService.get('userTenantPermits')
  }

  getIsTenant() {
    return this.StorageService.get('isTenant');
  }

  getIdToken() {
    return this.StorageService.get('idToken');
  }

  getIsBeamAuth() {
    return this.StorageService.get('isBeamAuth');
  }

  getSelectedTenantCode() {
    return this.StorageService.get('selectedTenantCode');
  }

  getIsPlatformAdmin() {
    return this.StorageService.get('isPA');
  }

  /**
   * 
   * @param {Object | String} googleAuthResponse  if OBject, it's the google sign in response, if string, it's the beam firebase user id token
   * @returns 
   */
  login(googleAuthResponse) {
    // differentiate between login from cookie vs firebase auth response, if cookie is verified, googleAuthResponse is undefined
    if (googleAuthResponse && typeof googleAuthResponse === 'string') {
      this.setIdToken(googleAuthResponse);

      return this.$http({url: `${process.env.__backendUrl__}/internal/v1/login`, method: 'GET'})
        .then(res => {
          const vm = this;
          const user = res.data.user;
          this.StorageService.set('distributors', res.data.managingOrgs);
          this.StorageService.set('distributorsAsAdmin', res.data.managingOrgsAsAdmin);
          this.StorageService.set('isAdmin', user.isAdministrator);
          this.StorageService.set('userEmail', user.email);
          this.StorageService.set('userKey', user.id);
          this.StorageService.set('userInfo', user);

          this.getIdentity().then(function(resp) {
            vm.StorageService.set('userTenantPermits',  _.get(resp, 'user.userTenantPermits'));
          }).catch(err => {
            console.error(`Failed to set permit on user change. (${err.message})`);
          })

          return res.data.managingOrgs.length != 0;
        })
        .catch((res) => {
          console.error(res);
          this.sweet.show('Oops...', "We couldn't log you in :(. Please try again or contact support", 'error');
        });
    } else {
      return firebase.auth().currentUser.getIdToken()
        .then(idToken => this.setIdToken(idToken))
        .then(() => this.$http({url: `${process.env.__backendUrl__}/internal/v1/login`, method: 'GET'}))
        .then(res => {
          const user = res.data.user;
          this.StorageService.set('distributors', res.data.managingOrgs);
          this.StorageService.set('distributorsAsAdmin', res.data.managingOrgsAsAdmin);
          this.StorageService.set('isAdmin', user.isAdministrator);
          this.StorageService.set('userEmail', user.email);
          this.StorageService.set('userKey', user.id);
          this.StorageService.set('userInfo', user);
          return res.data.managingOrgs.length != 0;
        })
        .then(() => this.getAuthV2())
        .then(authV2Resp => {
          const isPlatformAdmin = _.get(authV2Resp, 'data.data.identity.platformAdminActive');
          this.StorageService.set('isPA', isPlatformAdmin);
        })
        .catch((res) => {
          console.error(res);
          this.sweet.show('Oops...', "We couldn't log you in :(. Please try again or contact support", 'error');
        });
    }
  }

  async logout() {
    this.disablePoll();
    this.StorageService.removeAll();
    await firebase.auth().signOut();

    if (this.getIsBeamAuth()) {
      await this.unifiedLoginService.clearSession();
      await this.unifiedLoginService.stopPolling();
    }
  }
}

